import { useEffect, useMemo, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useCachedState } from "@/lib/swr";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { V } from "@/lib/vocab";
import { CommunityCard } from "@/components/CommunityCard";
import { CommunityMark } from "@/components/CommunityMark";
import { ManagedBy } from "@/components/ManagedBy";
import { PulseChip } from "@/components/pulse/PulseChip";
import { usePulseBadge } from "@/components/pulse/usePulseBadge";
import { toast } from "sonner";
import {
  Building2, MessagesSquare, AlertTriangle, Smile, Phone,
  ArrowRight, Sparkles, Plus, X, Loader2, LayoutGrid, List, Search,
} from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#E5E0D8] shadow-[0_1px_2px_rgba(0,0,0,0.04)]";
const EYEBROW = "text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8378]";
const WINDOWS = [[7, "7d"], [30, "30d"], [90, "90d"]];
const VIEW_KEY = "askredline.portfolioHomeView";

function MetricStrip({ cells }) {
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-[#EDE8E0]">
        {cells.map((c) => (
          <div key={c.key} data-testid={`portfolio-metric-${c.key}`} className="bg-white px-5 py-4">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">
              <c.icon className={`h-3.5 w-3.5 ${c.iconCls}`} strokeWidth={2} /> {c.label}
            </div>
            <div className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-2 leading-none">
              {c.value ?? "\u2014"}{c.value != null && c.suffix ? <span className="text-lg text-[#78716C]">{c.suffix}</span> : null}
            </div>
            {c.sub && <div className="text-[11px] text-[#A8A29E] mt-1 truncate">{c.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// One community as a quiet row — the list view of the Overview.
function CommunityRow({ t, c, days, attention, enterCommunity, pulseChip }) {
  return (
    <div data-testid={`portfolio-row-${t.id}`} className="flex items-center gap-4 px-4 py-3 border-b border-[#EDE8E0] last:border-0 hover:bg-sand-50/60 transition-colors">
      <button onClick={() => enterCommunity(t.id)} className="shrink-0 rounded-lg" title={`Open ${t.name}`}>
        <CommunityMark tenant={t} size={36} rounded="rounded-lg" flagged={attention} testid={`portfolio-row-mark-${t.id}`} />
      </button>
      <button onClick={() => enterCommunity(t.id)} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-heading font-bold text-sm text-[#1C1917] truncate">{t.name}</span>
          {attention && <span data-testid={`portfolio-row-attention-${t.id}`} className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-redline-600"><span className="h-1.5 w-1.5 rounded-full bg-redline-500 animate-pulse" /> Needs attention</span>}
        </div>
        <div className="flex items-center gap-2 text-xs text-[#78716C] min-w-0">
          <span className="truncate" style={{ color: t.brand?.color || undefined }}>{t.persona_name}</span>
          <ManagedBy tenant={t} className="hidden md:inline-flex" />
        </div>
      </button>
      <div className="hidden lg:flex items-center gap-4 text-xs text-[#78716C] shrink-0">
        <span className={`w-28 text-right tabular-nums ${c.escalations > 0 ? "text-amber-700 font-semibold" : ""}`} title={`Escalations, last ${days} days`}>{c.escalations > 0 ? `${c.escalations} escalated` : `${c.conversations} conv.`}</span>
      </div>
      <span className="shrink-0">{pulseChip}</span>
      <ArrowRight className="h-4 w-4 text-[#A8A29E] shrink-0" />
    </div>
  );
}

export default function PortfolioHome() {
  const { enterCommunity, viewCompanyId, role, tenants, refreshTenants } = useDash();
  const [pulseSnaps, reloadPulse] = usePulseBadge(viewCompanyId);
  const tenantById = useMemo(() => Object.fromEntries((tenants || []).map((t) => [t.id, t])), [tenants]);
  const [days, setDays] = useState(30);
  const [query, setQuery] = useState("");
  const [view, setViewState] = useState(() => { try { return localStorage.getItem(VIEW_KEY) || "cards"; } catch { return "cards"; } });
  const setView = (v) => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ } };
  const [data, setData] = useCachedState(`pf-ov:${viewCompanyId || ""}:${days}`, null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", persona_name: "" });
  const [saving, setSaving] = useState(false);
  const canAdd = role === "company_admin";

  const createCommunity = async () => {
    const name = addForm.name.trim();
    if (!name) { toast.error(`${V.one("org")} name is required`); return; }
    setSaving(true);
    try {
      await api.post("/tenants", { name, persona_name: addForm.persona_name.trim() || `Ask ${name}` });
      toast.success(`${V.one("org")} created — pending activation. Redline will attach a phone number to go live.`);
      setShowAdd(false);
      setAddForm({ name: "", persona_name: "" });
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't create that ${V.lc("org")}`);
    } finally { setSaving(false); }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const cq = viewCompanyId ? `&company_id=${viewCompanyId}` : "";
    api.get(`/portfolio/overview?days=${days}${cq}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days, viewCompanyId, reloadKey]);

  const t = data?.totals || {};
  const needsIds = useMemo(() => new Set((data?.needs_attention || []).map((c) => c.tenant_id)), [data]);
  // Communities that need you float to the top; the rest keep the server's order.
  const communities = useMemo(() => {
    const all = (data?.communities || []).map((c) => ({ c, t: tenantById[c.tenant_id] || { id: c.tenant_id, name: c.name, stats: { conversations: c.conversations, escalations: c.escalations, open_requests: c.open_requests } }, attention: needsIds.has(c.tenant_id) }));
    return [...all.filter((x) => x.attention), ...all.filter((x) => !x.attention)];
  }, [data, tenantById, needsIds]);
  // Administrators can run 1,300+ communities — search by community, assistant or manager name.
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return communities;
    return communities.filter(({ t: tt }) =>
      (tt.name || "").toLowerCase().includes(q) ||
      (tt.persona_name || "").toLowerCase().includes(q) ||
      (tt.managers || []).some((m) => (m.name || "").toLowerCase().includes(q))
    );
  }, [communities, q]);
  const unassigned = data?.unassigned_communities || [];
  const [assigningId, setAssigningId] = useState("");
  const assignToPortfolio = async (tid) => {
    const targetCompany = viewCompanyId || data?.company_id;
    if (!targetCompany) { toast.error("Open this portfolio directly to assign communities"); return; }
    setAssigningId(tid);
    try {
      await api.put(`/tenants/${tid}/company`, { company_id: targetCompany });
      toast.success("Community assigned to this portfolio");
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign"); }
    finally { setAssigningId(""); }
  };

  const cells = [
    { key: "communities", icon: Building2, iconCls: "text-[#78716C]", label: V.many("org"), value: t.communities },
    { key: "conversations", icon: MessagesSquare, iconCls: "text-blue-500", label: "Conversations", value: t.conversations, sub: `last ${days} days` },
    { key: "auto", icon: Sparkles, iconCls: "text-redline-500", label: "Auto-answered", value: t.auto_answered_pct, suffix: "%" },
    { key: "csat", icon: Smile, iconCls: "text-emerald-500", label: "Satisfaction", value: t.csat_pct, suffix: "%" },
    { key: "escalations", icon: AlertTriangle, iconCls: "text-amber-500", label: "Escalations", value: t.escalations, sub: `last ${days} days` },
  ];
  const pulseFor = (tt) => <PulseChip tenant={tt} snap={pulseSnaps[tt.id]} role={role} onOpen={() => enterCommunity(tt.id, "/dashboard/pulse")} onTurnedOn={async () => { await refreshTenants(); reloadPulse(); }} />;

  return (
    <div className="max-w-[1400px] space-y-8" data-testid="portfolio-home">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className={EYEBROW + " text-redline-600 mb-1.5"}>Portfolio</div>
          <PageTitle title="Overview" about={`Every ${V.lc("org")} you manage, at a glance.`} />
        </div>
        <div className="flex items-center gap-2">
          {canAdd && (
            <button data-testid="portfolio-add-community" onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white font-semibold px-4 py-2 text-sm hover:bg-redline-600 transition-colors">
              <Plus className="h-4 w-4" strokeWidth={2.25} /> Add {V.lc("org")}
            </button>
          )}
          <div className={`flex gap-1 ${CARD} p-1 rounded-full`}>
            {WINDOWS.map(([v, l]) => (
              <button key={v} data-testid={`portfolio-window-${v}`} onClick={() => setDays(v)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${days === v ? "bg-ink text-white" : "text-[#57534E] hover:bg-sand-100"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="add-community-dialog" onClick={() => !saving && setShowAdd(false)}>
          <div className={`${CARD} w-full max-w-md p-6`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-heading text-xl font-bold text-[#1C1917]">Add {V.a("org")}</h3>
                <p className="text-sm text-[#78716C] mt-1">It starts in <span className="font-semibold">Pending activation</span> — Redline attaches a phone number to take it live.</p>
              </div>
              <button data-testid="add-community-close" onClick={() => setShowAdd(false)} className="text-[#A8A29E] hover:text-ink"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-semibold text-[#44403C]">{V.one("org")} name</label>
                <input data-testid="add-community-name" autoFocus value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="e.g. Maplewood Community HOA"
                  className="mt-1.5 w-full rounded-xl bg-sand-50 border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:border-ink" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#44403C]">Assistant name <span className="font-normal text-[#A8A29E]">(optional)</span></label>
                <input data-testid="add-community-persona" value={addForm.persona_name} onChange={(e) => setAddForm({ ...addForm, persona_name: e.target.value })}
                  placeholder="e.g. Ask Maplewood"
                  className="mt-1.5 w-full rounded-xl bg-sand-50 border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:border-ink" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button data-testid="add-community-cancel" onClick={() => setShowAdd(false)} disabled={saving}
                className="rounded-full px-4 py-2 text-sm font-semibold text-[#57534E] hover:bg-sand-100 transition-colors">Cancel</button>
              <button data-testid="add-community-submit" onClick={createCommunity} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white font-semibold px-5 py-2 text-sm hover:bg-redline-600 transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create {V.lc("org")}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          <MetricStrip cells={cells} />

          {role === "super_admin" && unassigned.length > 0 && (
            <div data-testid="unassigned-communities" className={`${CARD} overflow-hidden border-amber-200`}>
              <div className="flex items-center gap-2 font-heading font-bold text-sm px-5 py-3.5 border-b border-amber-200 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4" strokeWidth={2} /> {unassigned.length} communit{unassigned.length === 1 ? "y" : "ies"} not assigned to a portfolio yet
              </div>
              {unassigned.map((c) => (
                <div key={c.tenant_id} data-testid={`unassigned-row-${c.tenant_id}`}
                  className="w-full flex items-center gap-4 px-5 py-3 border-b border-[#EDE8E0] last:border-0 text-left">
                  <div className="h-8 w-8 rounded-lg bg-sand-100 flex items-center justify-center shrink-0"><Building2 className="h-4 w-4 text-ink-600" strokeWidth={1.75} /></div>
                  <div className="flex-1 min-w-0 font-semibold text-[#1C1917] truncate">{c.name}</div>
                  <button data-testid={`assign-portfolio-${c.tenant_id}`}
                    onClick={() => assignToPortfolio(c.tenant_id)}
                    disabled={assigningId === c.tenant_id || !(viewCompanyId || data?.company_id)}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white font-semibold px-3.5 py-1.5 text-xs hover:bg-redline-600 transition-colors disabled:opacity-60">
                    {assigningId === c.tenant_id ? <><Loader2 className="h-3 w-3 animate-spin" /> Assigning…</> : <><Plus className="h-3 w-3" strokeWidth={2.5} /> Assign to this portfolio</>}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h2 className={EYEBROW}>Your {V.lcs("org")}</h2>
              {needsIds.size > 0 && (
                <span data-testid="portfolio-attention-count" className="inline-flex items-center gap-1.5 text-[11px] font-bold text-redline-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-redline-500 animate-pulse" /> {needsIds.size} need{needsIds.size === 1 ? "s" : ""} attention <span className="font-medium text-[#8A8378]">· shown first</span>
                </span>
              )}
              <span className="h-px flex-1 bg-[#E5E0D8] hidden sm:block" />
              <div className="relative flex-1 sm:flex-none sm:w-64 min-w-[160px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A8A29E]" strokeWidth={2} />
                <input data-testid="portfolio-home-search" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${V.lcs("org")}, assistant or manager…`}
                  className="w-full rounded-full border border-sand-200 bg-white pl-8 pr-8 py-1.5 text-sm outline-none focus:border-ink transition-colors" />
                {query && (
                  <button data-testid="portfolio-home-search-clear" onClick={() => setQuery("")} title="Clear search"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-ink"><X className="h-3.5 w-3.5" /></button>
                )}
              </div>
              <div className="flex gap-0.5 rounded-full border border-sand-200 bg-white p-0.5" data-testid="portfolio-home-view-toggle">
                <button data-testid="portfolio-home-view-cards" onClick={() => setView("cards")} title="Cards" aria-pressed={view === "cards"}
                  className={`rounded-full p-1.5 transition-colors ${view === "cards" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}><LayoutGrid className="h-3.5 w-3.5" strokeWidth={2} /></button>
                <button data-testid="portfolio-home-view-list" onClick={() => setView("list")} title="List" aria-pressed={view === "list"}
                  className={`rounded-full p-1.5 transition-colors ${view === "list" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}><List className="h-3.5 w-3.5" strokeWidth={2} /></button>
              </div>
            </div>
            {communities.length === 0 ? (
              <div className={`${CARD} p-10 text-center text-[#78716C]`} data-testid="portfolio-empty">
                No {V.lcs("org")} are assigned to your portfolio yet. Ask your Ask Redline contact to add them.
              </div>
            ) : filtered.length === 0 ? (
              <div className={`${CARD} p-10 text-center text-[#78716C]`} data-testid="portfolio-home-no-match">
                No {V.lcs("org")} match <span className="font-semibold text-ink">"{query}"</span>.
                <button data-testid="portfolio-home-no-match-clear" onClick={() => setQuery("")} className="ml-1.5 text-redline-600 font-semibold hover:underline">Clear search</button>
              </div>
            ) : view === "list" ? (
              <div className={`${CARD} overflow-hidden`} data-testid="portfolio-home-communities-list">
                {filtered.map(({ c, t: tt, attention }) => (
                  <CommunityRow key={c.tenant_id} t={tt} c={c} days={days} attention={attention} enterCommunity={enterCommunity} pulseChip={pulseFor(tt)} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="portfolio-home-communities">
                {filtered.map(({ c, t: tt, attention }) => (
                  <CommunityCard key={c.tenant_id} tenant={tt} enterCommunity={enterCommunity} testid={`portfolio-community-${c.tenant_id}`} pulseChip={pulseFor(tt)} showStats={false}
                    footer={(
                      <div className="mt-auto px-4 pb-4 pt-1 flex items-center justify-between gap-3 text-xs text-[#78716C]">
                        <span className="truncate" data-testid={`portfolio-community-window-${c.tenant_id}`}>
                          {attention && <span className="font-bold text-redline-600 mr-1.5">Needs attention ·</span>}
                          Last {days} days: <span className="font-semibold text-ink tabular-nums">{c.conversations}</span> conversations
                          {c.escalations > 0 && <> · <span className="font-semibold text-amber-700 tabular-nums">{c.escalations}</span> escalated</>}
                        </span>
                        <button data-testid={`portfolio-community-open-${c.tenant_id}`} onClick={() => enterCommunity(c.tenant_id)}
                          className="shrink-0 rounded-full bg-ink text-white hover:bg-redline-500 transition-colors font-semibold px-3.5 py-1.5 text-xs flex items-center gap-1">
                          Open <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    )} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
